import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import OrgAutocomplete from '../OrgAutocomplete';
import { PLACING_ORG_TYPES } from '../../lib/orgTypes';

/**
 * A holding organisation ("Council not yet known"): which council was it?
 * Confirm one the records name, type one, or merge these placements into a
 * council or agency the person already has (POST /organisations/{id}/resolve).
 */
export const ResolveCouncil = ({ org, editing }) => {
  const [form, setForm] = useState({ type: 'council', name: '', canonical: null, mergeInto: '' });
  const suggestions = (org.pending_suggestions || []).filter((s) => s.type === 'resolve_council');
  const mergeTargets = editing.allOrgs.filter((o) => PLACING_ORG_TYPES.includes(o.type) && !o.unresolved && o.id !== org.id);

  const submit = () => {
    if (!form.mergeInto && !form.name.trim()) {
      toast.error('Name the council, or choose one of yours to merge into.');
      return;
    }
    editing.resolveCouncil(form.mergeInto
      ? { merge_into_org_id: form.mergeInto }
      : { name: form.name.trim(), type: form.type, canonical_org_id: form.canonical || null });
  };

  return (
    <div className="rounded-lg bg-amber-wash px-5 py-5 space-y-4" data-testid="org-unresolved-banner">
      <div>
        <p className="text-[15px] font-semibold text-amber-ink">Which council was responsible for you here?</p>
        <p className="text-sm text-ink-70 mt-1 max-w-[68ch]">
          This holds the homes{org.location ? ` in ${org.location}` : ''} whose council you didn’t know when you added them. Nothing can be
          sent to it. Records from a school or GP almost always name the council — when one does, we’ll suggest it here.
        </p>
      </div>
      {suggestions.length > 0 && (
        <div className="space-y-2" data-testid="org-resolve-suggestions">
          <p className="pd-eyebrow text-amber-ink">Found in your records</p>
          {suggestions.map((sg) => (
            <div key={sg.id} className="rounded-md bg-surface px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
              <p className="flex-1 text-[15px] text-ink">Was <span className="font-semibold">{sg.name}</span> the council that placed you?</p>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="ghost" disabled={editing.saving} onClick={() => editing.rejectSuggestions([sg.id])}>Not this one</Button>
                <Button size="sm" disabled={editing.saving} onClick={() => editing.confirmSuggestions([sg.id])} data-testid={`confirm-resolve-${sg.id}`}>Yes, that was the council</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="org-resolve-form">
        <div className="space-y-2">
          <Label className="text-[15px] font-semibold">Name the council or agency</Label>
          <div className="flex gap-2">
            {[['council', 'Council'], ['fostering_agency', 'Agency']].map(([v, l]) => (
              <button key={v} type="button" aria-pressed={form.type === v}
                onClick={() => setForm({ ...form, type: v, name: '', canonical: null, mergeInto: '' })}
                className={`rounded-full px-3 py-1.5 text-sm font-medium border ${form.type === v ? 'bg-brand-wash text-brand-ink border-brand-wash' : 'bg-surface text-ink-70 border-rule'}`}>
                {l}
              </button>
            ))}
          </div>
          <OrgAutocomplete
            value={form.name}
            type={form.type}
            onChange={(v) => setForm({ ...form, name: v, canonical: null, mergeInto: '' })}
            onPick={(picked) => setForm({ ...form, name: picked.name, canonical: picked.id, mergeInto: '' })}
            placeholder={form.type === 'council' ? 'For example, Birmingham City Council' : 'For example, Foster Care Associates'}
            testId="org-resolve-name"
          />
        </div>
        {mergeTargets.length > 0 && (
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold">Or one you already have</Label>
            <p className="text-sm text-ink-55">Its homes move across and this placeholder goes.</p>
            <Select value={form.mergeInto} onValueChange={(v) => setForm({ ...form, mergeInto: v, name: '', canonical: null })}>
              <SelectTrigger className="bg-surface border-surface" aria-label="Merge into" data-testid="org-resolve-merge">
                <SelectValue placeholder="Choose an organisation" />
              </SelectTrigger>
              <SelectContent>
                {mergeTargets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div>
        <Button onClick={submit} disabled={editing.saving || (!form.mergeInto && !form.name.trim())} data-testid="org-resolve-btn">
          {editing.saving && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}
          {form.mergeInto ? 'Move the homes there' : 'This was the council'}
        </Button>
      </div>
    </div>
  );
};

export default ResolveCouncil;
